import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ExternalLink, Filter as FilterIcon, Users, ChevronRight, ChevronDown, ArrowUp, ArrowDown, ChevronsUpDown, GripVertical, Eye, EyeOff, Calculator, AlertTriangle } from "lucide-react";
import InvoiceOriginStatus from "@/components/InvoiceOriginStatus";
import { formatIDR, PROJECT_STATUS, deriveProjectStatus } from "@/lib/constants";
import api from "@/lib/api";
import OverReceivedBadge from "@/components/OverReceivedBadge";
import { SisaWaktuBadge } from "@/components/projects/SisaWaktuBadge";
import ProfitStatusBadge from "@/components/cost-control/ProfitStatusBadge";
import ProfitDrilldownDialog, { buildUnknownProfitView } from "@/components/cost-control/ProfitDrilldownDialog";

// 2026-06-20 (user request): Status OPERASIONAL project (pekerjaan, penagihan
// & retensi) — satu aturan dari backend `operational_status`, badge + filter.
import { OpStatusBadge, opStatusOf, OP_STATUS_META, OP_STATUS_ORDER } from "@/lib/operationalStatus";

/**
 * SummaryProjectListView — full-width daftar PARENT project (sub VO/WO jadi
 * breakdown expandable). 2026-06-19 (user request):
 *  - Filter MULTI-PILIH untuk Client & PIC dengan mode Tampilkan (include) atau
 *    Sembunyikan (exclude) pilihan. Status multi-pilih (include).
 *  - Header kolom bisa DI-DRAG untuk geser posisi kiri/kanan (persist
 *    localStorage "ens_summary_col_order").
 *  - Header kolom bisa di-KLIK untuk sort asc/desc.
 * R228 (2026-10-20, user request): fitur PENILAIAN RISIKO dinonaktifkan —
 * badge Aman/Perhatian/Terlambat & filter Risiko dihapus dari tampilan.
 * deriveRisk/RISK_META dihapus (murni derivasi frontend; tidak ada data DB
 * yang dihapus — fitur dapat diaktifkan kembali).
 */
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500];
const LS_COLS = "ens_summary_col_order";

const COLUMNS = [
  { key: "name",     label: "Nama Proyek",         thCls: "w-[20%]", tdCls: "px-3 py-2 align-top", sortable: true },
  { key: "client",   label: "Client",              thCls: "w-[13%]", tdCls: "px-3 py-2 align-top break-words", sortable: true },
  { key: "contract", label: "Nilai Kontrak",       thCls: "w-[13%]", tdCls: "px-3 py-2 align-top text-right tabular-nums", sortable: true },
  { key: "phys",     label: "Progress Pekerjaan",   thCls: "w-[10%]", tdCls: "px-3 py-2 align-top", sortable: true },
  { key: "pay",      label: "Progress Pembayaran", thCls: "w-[11%]", tdCls: "px-3 py-2 align-top", sortable: true },
  // 2026-10-18 (user request): kolom Piutang — 4 status terpisah per proyek
  // (engine A.R., sama dgn Dasbor/Collection/Monitoring). Tiap sel dapat
  // diklik → daftar invoice pembentuk angka di Collection.
  { key: "ar",       label: "Piutang (4 Status)",   thCls: "w-[15%]", tdCls: "px-3 py-2 align-top", sortable: false },
  { key: "cash",     label: "Posisi Kas",          thCls: "w-[13%]", tdCls: "px-3 py-2 align-top text-right tabular-nums", sortable: true },
  { key: "days",     label: "Sisa Waktu",          thCls: "w-[9%]",  tdCls: "px-3 py-2 align-top", sortable: true },
  { key: "profit",   label: "Selisih (Pengendalian Biaya)", thCls: "w-[11%]", tdCls: "px-3 py-2 align-top text-right tabular-nums", sortable: true },
  { key: "aksi",     label: "Aksi",                thCls: "w-[12%]", tdCls: "pl-3 pr-2 py-2 align-top text-right", sortable: false },
];

// Sumber kanonis: field `schedule` dari backend (project_schedule_engine,
// Asia/Jakarta) — frontend TIDAK menghitung ulang selisih tanggal.
// Return negatif = terlambat (dipertahankan untuk keperluan sort).
function daysRemaining(p) {
  const s = p.schedule;
  if (!s) return null;
  const st = s.schedule_status;
  if (["not_applicable", "cancelled", "no_end_date", "no_schedule", "completed_missing_date"].includes(st)) return null;
  if ((s.overdue_days || 0) > 0) return -(s.overdue_days);
  if (st && st.startsWith("completed")) return 0;
  return s.remaining_days ?? null;
}

/** MultiFilter — dropdown checkbox multi-pilih. withMode=true menampilkan
 *  toggle Tampilkan/Sembunyikan pilihan (exclude). */
function MultiFilter({ label, options, sel, mode, onSelChange, onModeChange, withMode, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const toggle = (v) => {
    const next = new Set(sel);
    if (next.has(v)) next.delete(v); else next.add(v);
    onSelChange(next);
  };
  const active = sel.size > 0;
  const shown = options.filter((o) => !q || o.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid={testid} onClick={() => setOpen((v) => !v)}
        className={`border py-1.5 px-2 text-xs bg-white inline-flex items-center gap-1.5 ${active ? "border-[#0B1D3A] text-[#0B1D3A] font-semibold" : "border-slate-300 text-slate-600"}`}>
        {label}{active ? ` (${sel.size})` : ""}
        {active && withMode && (mode === "exclude" ? <EyeOff size={11} className="text-rose-600" /> : <Eye size={11} className="text-emerald-600" />)}
        <ChevronDown size={12} className="text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-64 bg-white border border-slate-300 shadow-lg" data-testid={`${testid}-panel`}>
          <div className="p-2 border-b border-slate-100">
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari…" autoFocus
              data-testid={`${testid}-search`}
              className="w-full border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:border-[#0B1D3A]" />
          </div>
          {withMode && (
            <div className="px-2 py-1.5 border-b border-slate-100 flex items-center gap-1" data-testid={`${testid}-mode`}>
              <button type="button" onClick={() => onModeChange("include")} data-testid={`${testid}-mode-include`}
                className={`flex-1 px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border ${mode !== "exclude" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-300"}`}>
                <Eye size={10} className="inline mr-1" />Tampilkan pilihan
              </button>
              <button type="button" onClick={() => onModeChange("exclude")} data-testid={`${testid}-mode-exclude`}
                className={`flex-1 px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border ${mode === "exclude" ? "bg-rose-600 text-white border-rose-600" : "bg-white text-slate-500 border-slate-300"}`}>
                <EyeOff size={10} className="inline mr-1" />Sembunyikan
              </button>
            </div>
          )}
          <div className="max-h-52 overflow-y-auto">
            {shown.length === 0 && <div className="px-3 py-3 text-[11px] text-slate-400 text-center">Tidak ada opsi.</div>}
            {shown.map((o) => (
              <label key={o} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 cursor-pointer" data-testid={`${testid}-opt-${o}`}>
                <input type="checkbox" checked={sel.has(o)} onChange={() => toggle(o)} className="accent-[#0B1D3A]" />
                <span className="truncate">{o}</span>
              </label>
            ))}
          </div>
          {active && (
            <div className="px-2 py-1.5 border-t border-slate-100 flex items-center justify-between">
              <span className="text-[10px] text-slate-400 tabular-nums">{sel.size} dipilih</span>
              <button type="button" onClick={() => onSelChange(new Set())} data-testid={`${testid}-clear`}
                className="text-[10px] text-rose-600 hover:underline uppercase tracking-wider">Bersihkan</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SummaryProjectListView({
  projects, loadState, loadError, onRetry, canCreate, onOpenNewProject,
}) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  // Multi-select filters — Set pilihan + mode include/exclude (Client & PIC).
  const [clientSel, setClientSel] = useState(new Set());
  const [clientMode, setClientMode] = useState("include");
  const [picSel, setPicSel] = useState(new Set());
  const [picMode, setPicMode] = useState("include");
  const [statusSel, setStatusSel] = useState(new Set());
  // 2026-06-20 (user request): filter Status OPERASIONAL (label → dipetakan ke key).
  const [opsSel, setOpsSel] = useState(new Set());
  // R228 (2026-10-20): filter Risiko DINONAKTIFKAN (fitur penilaian risiko
  // dinonaktifkan sementara — lihat header file).
  const [fPeriod, setFPeriod] = useState({ from: "", to: "" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // 2026-06-20 (user request): peta ringkasan Cost Control per proyek —
  // dipakai kolom "Profit (Cost Control)" (parent = agregasi parent + sub).
  // R227: entri menyertakan profit_view kanonis engine (lihat kolom Selisih).
  const [ccMap, setCcMap] = useState({});
  const [ccLoaded, setCcLoaded] = useState(false);   // R227: bedakan "belum termuat" vs "tidak ada data CC"
  const [ccDenied, setCcDenied] = useState(false);   // R227: user tanpa akses finansial (403) → fallback lama
  // R229: pisah state open vs pid — saat ditutup, pid DIPERTAHANKAN supaya isi
  // dialog tidak berubah menjadi empty state selama animasi penutupan.
  const [profitDrill, setProfitDrill] = useState(null);
  const [profitDrillOpen, setProfitDrillOpen] = useState(false);
  const openProfitDrill = (pid) => { setProfitDrill(pid); setProfitDrillOpen(true); };
  useEffect(() => {
    let alive = true;
    api.get("/cost-control/summaries")
      .then((res) => { if (alive) { setCcMap(res.data?.summaries || {}); setCcLoaded(true); } })
      .catch((e) => { if (alive) { setCcDenied(e?.response?.status === 403); setCcLoaded(true); } });
    return () => { alive = false; };
  }, []);

  // 2026-10-18 (user request): ringkasan piutang per proyek — SATU endpoint
  // engine A.R. (sumber sama dgn Dasbor/Collection/Monitoring). Bila user
  // tidak punya akses finansial (403), peta kosong → sel tampil "—".
  const [arMap, setArMap] = useState({ per_project: {}, per_head: {} });
  useEffect(() => {
    let alive = true;
    api.get("/active-projects/ar-summary")
      .then((res) => { if (alive) setArMap(res.data || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Kolom: urutan (drag) + sort (klik header).
  const [colOrder, setColOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(LS_COLS) || "null");
      if (Array.isArray(saved) && saved.length === COLUMNS.length && saved.every((k) => COLUMNS.some((c) => c.key === k))) return saved;
    } catch { /* noop */ }
    return COLUMNS.map((c) => c.key);
  });
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const dragKey = useRef(null);
  const dropCol = (target) => {
    const src = dragKey.current;
    if (!src || src === target) return;
    setColOrder((prev) => {
      const arr = [...prev];
      const from = arr.indexOf(src), to = arr.indexOf(target);
      if (from < 0 || to < 0) return prev;
      arr.splice(to, 0, arr.splice(from, 1)[0]);
      try { localStorage.setItem(LS_COLS, JSON.stringify(arr)); } catch { /* noop */ }
      return arr;
    });
  };
  const clickSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  const rows = useMemo(() => projects.filter((p) => p.project_type !== "internal"), [projects]);
  const { parents, childrenOf, orphans } = useMemo(() => {
    const ps = rows.filter((p) => p.project_type === "new_project" && !p.parent_project_id);
    const pIds = new Set(ps.map((p) => p.id));
    const map = {};
    const orph = [];
    for (const p of rows) {
      if (pIds.has(p.id)) continue;
      const pid = p.parent_project_id;
      if (pid && pIds.has(pid)) (map[pid] = map[pid] || []).push(p);
      else orph.push(p);
    }
    return { parents: ps, childrenOf: map, orphans: orph };
  }, [rows]);

  const clients = useMemo(() => [...new Set(rows.map((p) => p.client).filter(Boolean))].sort(), [rows]);
  const pics = useMemo(() => [...new Set(rows.map((p) => p.pic_name || p.created_by_name).filter(Boolean))].sort(), [rows]);

  // Set multi-filter: include = tampilkan hanya yg dipilih; exclude = sembunyikan yg dipilih.
  const applySet = (val, sel, mode) => {
    if (sel.size === 0) return true;
    return mode === "exclude" ? !sel.has(val) : sel.has(val);
  };

  const matches = useMemo(() => {
    const qlc = q.trim().toLowerCase();
    return (p) => {
      if (qlc) {
        const hay = [p.project_name, p.client, p.wo_number, p.po_number, p.variation_order_name, p.pic_name, p.created_by_name].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(qlc)) return false;
      }
      if (!applySet(p.client, clientSel, clientMode)) return false;
      const rowPic = p.pic_name || p.created_by_name;
      if (!applySet(rowPic, picSel, picMode)) return false;
      if (statusSel.size > 0 && !statusSel.has(deriveProjectStatus(p))) return false;
      if (opsSel.size > 0 && !opsSel.has(OP_STATUS_META[opStatusOf(p).key].label)) return false;
      const startRaw = p.project_type === "variation_order" ? p.vo_start_date : p.start_date;
      if (fPeriod.from && (!startRaw || String(startRaw).slice(0, 10) < fPeriod.from)) return false;
      if (fPeriod.to && (!startRaw || String(startRaw).slice(0, 10) > fPeriod.to)) return false;
      return true;
    };
  }, [q, clientSel, clientMode, picSel, picMode, statusSel, opsSel, fPeriod]);

  const filtered = useMemo(() => [...parents.filter(matches), ...orphans.filter(matches)], [parents, orphans, matches]);

  // Agregat parent (selaras endpoint /monitoring: parent + semua sub VO/WO).
  const aggOf = (p) => {
    const ch = childrenOf[p.id] || [];
    // 2026-06-20 (user request): NILAI KONTRAK tampil = data induk (canonical).
    // Agregasi nilai sub (WO/VO) ditampilkan terpisah berlabel "Total Nilai WO/VO"
    // + peringatan bila berbeda. cv efektif (induk>0 ? induk : agregasi) hanya
    // dipakai sebagai basis kalkulasi progress pembayaran, BUKAN tampilan.
    const cvParent = Number(p.contract_value || 0);
    const subSum = ch.reduce((a, c) => a + Number(c.contract_value || 0), 0);
    // 2026-06-21 (user request): Nilai Kontrak parent = JUMLAHAN — umum: induk
    // + VO; maintenance: Σ WO (induk tanpa nilai sendiri). Bukan lagi induk saja.
    const cv = cvParent + subSum;
    const inc = Number(p.total_income || 0) + ch.reduce((a, c) => a + Number(c.total_income || 0), 0);
    const payc = Number(p.payment_credit ?? p.total_income ?? 0) + ch.reduce((a, c) => a + Number(c.payment_credit ?? c.total_income ?? 0), 0);
    const exp = Number(p.total_expense || 0) + ch.reduce((a, c) => a + Number(c.total_expense || 0), 0);
    const wos = ch.filter((c) => c.project_type === "working_order");
    // 2026-06-20 (user request): definisi seragam dgn Monitoring Project —
    // WO dibatalkan keluar dari denominator progress; "selesai" = closed /
    // selesai fisik (project_done_date) / invoice terkirim.
    const woCancelled = wos.filter((c) => c.status === "cancelled" || c.status === "cancelled_by_owner").length;
    const woDone = wos.filter((c) => c.status === "completed" || c.project_done_date || c.invoice_sent_date).length;
    const woEff = Math.max(0, wos.length - woCancelled);
    const subLabel = wos.length > 0 ? "WO" : ch.length > 0 ? "VO" : "WO";
    const phys = p.is_outlet
      ? (woEff > 0 ? (woDone / woEff) * 100 : 0)
      : Number(p.physical_progress_pct || 0);
    return { cv, cvParent, subSum, subLabel, inc, payc, exp, phys, woDone, woEff, nVO: ch.filter((c) => c.project_type === "variation_order").length, nWO: wos.length };
  };
  const aggMap = useMemo(() => Object.fromEntries(filtered.map((p) => [p.id, aggOf(p)])), [filtered, childrenOf]); // eslint-disable-line
  const totalSubCount = useMemo(() => filtered.reduce((a, p) => a + ((childrenOf[p.id] || []).length), 0), [filtered, childrenOf]);

  const SORT_VAL = {
    name: (p) => (p.project_name || "").toLowerCase(),
    client: (p) => (p.client || "").toLowerCase(),
    contract: (p) => aggMap[p.id]?.cv ?? 0,
    phys: (p) => aggMap[p.id]?.phys ?? 0,
    // 2026-10-22 (user request "TUNTASKAN SINKRONISASI"): sort Progress
    // Pembayaran ikut field kanonis (received_official ÷ dasar resmi).
    pay: (p) => { const b = p.payment_breakdown; if (b && (b.official_collectible_amount || 0) > 0) return (b.received_official || 0) / b.official_collectible_amount; const a = aggMap[p.id]; return a && a.cv > 0 ? (a.payc ?? a.inc) / a.cv : 0; },
    cash: (p) => { const a = aggMap[p.id]; return a ? a.inc - a.exp : 0; },
    days: (p) => daysRemaining(p) ?? Number.MAX_SAFE_INTEGER,
    profit: (p) => {
      const ids = [p.id, ...((childrenOf[p.id] || []).map((k) => k.id))];
      let s = 0; let has = false;
      for (const id of ids) { const c = ccMap[id]; if (c && c.item_count > 0) { has = true; s += Number(c.profit || 0); } }
      return has ? s : Number.NEGATIVE_INFINITY;
    },
  };
  const sorted = useMemo(() => {
    if (!sort.key || !SORT_VAL[sort.key]) return filtered;
    const get = SORT_VAL[sort.key];
    return [...filtered].sort((a, b) => {
      const va = get(a), vb = get(b);
      const cmp = typeof va === "string" ? va.localeCompare(vb) : va - vb;
      return cmp * sort.dir;
    });
  }, [filtered, sort, aggMap, ccMap]); // eslint-disable-line

  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const curPage = Math.min(page, totalPages);
  const paged = sorted.slice((curPage - 1) * pageSize, curPage * pageSize);

  // 16-OVERRECEIVED-R1: engine kanonik PaymentReceipt (`/invoices/payment-progress`)
  // sebagai sumber flag over-received + label klasifikasi tersimpan. Hanya untuk
  // baris ter-render (parent halaman aktif + children) agar payload kecil.
  const [overPgMap, setOverPgMap] = useState({});
  useEffect(() => {
    const ids = [];
    for (const p of paged) {
      ids.push(p.id);
      for (const k of (childrenOf[p.id] || [])) ids.push(k.id);
    }
    if (ids.length === 0) { setOverPgMap({}); return; }
    let alive = true;
    api.post("/invoices/payment-progress", { project_ids: ids.slice(0, 500), aggregate_ids: [] })
      .then((res) => { if (alive) setOverPgMap(res.data?.per_project || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, [paged, childrenOf]); // eslint-disable-line

  const resetFilters = () => { setQ(""); setClientSel(new Set()); setClientMode("include"); setPicSel(new Set()); setPicMode("include"); setStatusSel(new Set()); setOpsSel(new Set()); setFPeriod({ from: "", to: "" }); setPage(1); };
  const hasAnyFilter = q || clientSel.size || picSel.size || statusSel.size || fPeriod.from || fPeriod.to;

  /** Konten sel per kolom — dipakai parent & child sesuai colOrder. */
  const cellFor = (r, key, ctx) => {
    const { isChild, kids, isOpen, agg } = ctx;
    const v = isChild
      ? { cv: Number(r.contract_value || 0), inc: Number(r.total_income || 0), exp: Number(r.total_expense || 0), phys: Number(r.physical_progress_pct || 0), payc: Number(r.payment_credit ?? r.total_income ?? 0), cvParent: Number(r.contract_value || 0), subSum: 0, subLabel: null }
      : { cv: agg.cv, inc: agg.inc, exp: agg.exp, phys: agg.phys, payc: agg.payc, cvParent: agg.cvParent, subSum: agg.subSum, subLabel: agg.subLabel };
    const cash = v.inc - v.exp;
    // 2026-06-21 (user request): Progress Pembayaran mengikuti NILAI INVOICE
    // — Pelunasan = diterima ÷ Kas Harus Diterima; sisa = Sisa Piutang.
    // Sumber: payment_breakdown backend (satu engine dgn Monitoring).
    const bdRow = r.payment_breakdown || null;
    // 2026-10-18 (closeout #3): piutang resmi = hanya invoice terverifikasi
    // FAT; draft dilacak terpisah (bukan piutang resmi, tidak memengaruhi
    // persentase progress pembayaran resmi).
    const pp = bdRow
      ? {
          // 2026-10-22 (user request "TUNTASKAN SINKRONISASI" §1): Progress
          // Pembayaran = engine kanonis — hanya received_official ÷ dasar
          // invoice resmi. DP/retensi/legacy/belum-terklasifikasi TIDAK ikut.
          receiptPct: bdRow.official_payment_progress ?? null,
          receiptRemaining: Math.max(0, (bdRow.official_collectible_amount ?? 0) - (bdRow.received_official ?? 0)),
          basisLabel: "Piutang Resmi",
          receiptOver: !!bdRow.receipt_over_official,
          draftRemaining: bdRow.draft_receivable ?? 0,
          draftCount: bdRow.draft_count ?? 0,
        }
      : { receiptPct: null, receiptRemaining: 0, basisLabel: "Piutang Resmi", receiptOver: false, draftRemaining: 0, draftCount: 0 };
    const payPct = pp.receiptPct != null ? pp.receiptPct : null;
    switch (key) {
      case "name": {
        const stKey = deriveProjectStatus(r);
        const st = PROJECT_STATUS[stKey] || PROJECT_STATUS.active;
        const typeBadge = r.project_type === "variation_order" ? "VO" : r.project_type === "working_order" ? "WO" : null;
        const primaryName = isChild
          ? (r.variation_order_name || (r.wo_number ? `WO ${r.wo_number}` : null) || r.project_name || "-")
          : (r.project_name || "-");
        return (
          <td key="name" className={`${COLUMNS[0].tdCls} ${isChild ? "pl-8" : ""}`}>
            <div className="font-semibold text-[#0B1D3A] min-w-0 flex items-center gap-1.5 flex-wrap">
              {!isChild && kids.length > 0 && (
                <button type="button" data-testid={`summary-expand-${r.id}`}
                  aria-label={isOpen ? "Tutup breakdown VO/WO" : "Buka breakdown VO/WO"} aria-expanded={isOpen}
                  onClick={() => toggleExpand(r.id)}
                  className="p-0.5 text-slate-400 hover:text-[#0B1D3A] shrink-0 transition-colors">
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
              )}
              {isChild && <span className="text-slate-300 shrink-0">└</span>}
              {typeBadge && (
                <span className={`inline-block px-1 py-0.5 text-[9px] font-bold tracking-wider border shrink-0 ${r.project_type === "variation_order" ? "bg-orange-50 border-orange-300 text-[#FF5A1F]" : "bg-sky-50 border-sky-300 text-sky-700"}`}>{typeBadge}</span>
              )}
              <span className="break-words whitespace-normal">{primaryName}</span>
              <span className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border ${st.cls || "bg-slate-50 border-slate-300 text-slate-700"}`} data-testid={`summary-status-${r.id}`}>{st.label}</span>
              {/* 2026-06-20 (user request): badge Status OPERASIONAL — tooltip = alasan status. */}
              <OpStatusBadge project={r} testid={`summary-op-status-${r.id}`} />
              {/* R228 (2026-10-20): badge risiko (Aman/Perhatian/Terlambat)
                  DINONAKTIFKAN — fitur penilaian risiko dinonaktifkan
                  sementara. Status operasional di atas BUKAN status risiko. */}
              {!isChild && kids.length > 0 && (
                <span className="text-[9px] text-slate-400 font-normal shrink-0" data-testid={`summary-subcount-${r.id}`}>{agg.nVO} VO · {agg.nWO} WO</span>
              )}
            </div>
            <div className="text-[10px] text-slate-500 mt-0.5 break-words">
              {isChild && r.project_name ? <span className="text-slate-400">{r.project_name} · </span> : null}
              {r.contract_number || r.po_number || `#${(r.id || "").slice(0, 6)}`}
              {" · PIC "}<span className="text-slate-700">{r.pic_name || r.created_by_name || "—"}</span>
            </div>
          </td>
        );
      }
      case "client":
        return <td key="client" className="px-3 py-2 align-top break-words whitespace-normal">{r.client || "—"}</td>;
      case "contract":
        // 2026-06-21 (user request): Nilai Kontrak parent = JUMLAHAN nilai
        // induk + VO (umum) / Σ WO (maintenance). Rincian tetap terbaca di
        // sub-baris; tanpa badge "beda" (total memang jumlahan).
        return (
          <td key="contract" className="px-3 py-2 align-top text-right tabular-nums" data-testid={`summary-contract-${r.id}`}>
            {v.cv > 0 ? formatIDR(v.cv) : <span className="text-slate-300">—</span>}
            {!isChild && v.subSum > 0 && (
              <div className="text-[9px] text-slate-500 tabular-nums" data-testid={`summary-sub-value-${r.id}`}>
                {v.cvParent > 0 ? `Induk ${formatIDR(v.cvParent)} + ` : ""}{v.subLabel} {formatIDR(v.subSum)}
              </div>
            )}
          </td>
        );
      case "phys":
        if (r.project_type === "working_order") {
          const done = r.status === "completed";
          return (
            <td key="phys" className="px-3 py-2 align-top" data-testid={`summary-work-progress-${r.id}`}>
              <span className={`inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border ${done ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-slate-50 border-slate-300 text-slate-600"}`}>
                {done ? "Sudah Selesai" : "Belum Selesai"}
              </span>
            </td>
          );
        }
        return (
          <td key="phys" className="px-3 py-2 align-top" data-testid={`summary-work-progress-${r.id}`}>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-slate-100"><div className="h-full bg-[#0B1D3A]" style={{ width: `${Math.min(100, v.phys)}%` }} /></div>
              <span className="text-[10px] tabular-nums text-slate-600 w-8 text-right">{v.phys.toFixed(0)}%</span>
            </div>
            {!isChild && r.is_outlet && agg.nWO > 0 && (
              <div className="mt-1 text-[9px] text-slate-500 tabular-nums" data-testid={`summary-work-progress-count-${r.id}`}>
                {agg.woDone} dari {agg.woEff} WO selesai
              </div>
            )}
          </td>
        );
      case "pay": {
        // 16-OVERRECEIVED-R1: flag dari engine kanonik PaymentReceipt; fallback
        // ke payment_breakdown bila entri progress belum termuat.
        const opg = overPgMap[r.id];
        const showOver = !!(opg?.over_received || pp.receiptOver);
        return (
          <td key="pay" className="px-3 py-2 align-top" data-testid={`summary-pay-${r.id}`}>
            {payPct == null ? (
              <>
                <span className="text-[10px] text-slate-400 italic">Belum dapat dihitung</span>
                <div className="mt-1"><InvoiceOriginStatus status={bdRow?.invoice_status} variant="blocks" testid={`summary-inv-status-${r.id}`} /></div>
                {pp.draftRemaining > 0 && (
                  <div className="mt-0.5 text-[9px] text-amber-700 tabular-nums" data-testid={`summary-pay-draft-${r.id}`}>
                    Draft Perlu Verifikasi: {formatIDR(pp.draftRemaining)} · {pp.draftCount} invoice (bukan piutang resmi)
                  </div>
                )}
                {bdRow?.legacy_unlinked_total > 0 && (
                  <div className="mt-0.5 text-[9px] text-amber-700 tabular-nums" data-testid={`summary-pay-legacy-${r.id}`}>
                    Penerimaan perlu diklasifikasi: {formatIDR(bdRow.legacy_unlinked_total)}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-slate-100"><div className={`h-full ${showOver ? "bg-amber-500" : "bg-emerald-600"}`} style={{ width: `${Math.min(100, payPct)}%` }} /></div>
                  <span className={`text-[10px] tabular-nums w-8 text-right ${showOver ? "text-amber-700 font-bold" : "text-emerald-700"}`}>{Math.round(payPct)}%</span>
                </div>
                <div className="mt-0.5 text-[9px] text-slate-500 tabular-nums">
                  {pp.basisLabel}: {formatIDR(pp.receiptRemaining)}
                </div>
                <div className="mt-1"><InvoiceOriginStatus status={bdRow?.invoice_status} variant="blocks" testid={`summary-inv-status-${r.id}`} /></div>
                {pp.draftRemaining > 0 && (
                  <div className="mt-0.5 text-[9px] text-amber-700 tabular-nums" data-testid={`summary-pay-draft-${r.id}`}>
                    Draft Perlu Verifikasi: {formatIDR(pp.draftRemaining)} · {pp.draftCount} invoice (bukan piutang resmi)
                  </div>
                )}
                {bdRow?.legacy_unlinked_total > 0 && (
                  <div className="mt-0.5 text-[9px] text-amber-700 tabular-nums" data-testid={`summary-pay-legacy-${r.id}`}>
                    Penerimaan perlu diklasifikasi: {formatIDR(bdRow.legacy_unlinked_total)}
                  </div>
                )}
              </>
            )}
            {showOver && (
              <div className="mt-0.5" data-testid={`summary-pay-over-${r.id}`}>
                <OverReceivedBadge
                  projectId={r.id}
                  overReceived={showOver}
                  excessAmount={Number(opg?.excess_amount ?? bdRow?.overpaid_total ?? 0)}
                  classification={opg?.classification || null}
                  onClassified={(pid, doc) => setOverPgMap((m) => ({ ...m, [pid]: { ...(m[pid] || {}), classification: doc } }))}
                />
              </div>
            )}
          </td>
        );
      }
      case "cash":
        return (
          <td key="cash" className={`px-3 py-2 align-top text-right tabular-nums ${cash >= 0 ? "text-[#0B1D3A]" : "text-rose-700"}`} data-testid={`summary-cash-${r.id}`}>
            {cash === 0 && v.inc === 0 && v.exp === 0 ? <span className="text-slate-300">—</span> : formatIDR(cash)}
          </td>
        );
      case "days":
        return (
          <td key="days" className="px-3 py-2 align-top">
            {/* Badge kanonis backend (Asia/Jakarta) + drill-down rincian jadwal. */}
            <SisaWaktuBadge schedule={r.schedule} testid={`summary-days-${r.id}`} />
          </td>
        );
      case "ar": {
        // 2026-10-18 (user request): 4 status piutang terpisah per proyek —
        // engine A.R. (sama dgn Dasbor/Collection/Monitoring). Parent =
        // agregasi induk + seluruh WO/VO anak (per_head); child = per_project.
        // Tiap kartu mini dapat diklik (mouse & keyboard — <button> native)
        // membuka daftar invoice pembentuk angka di Collection.
        const ar = (isChild ? (arMap.per_project || {})[r.id] : (arMap.per_head || {})[r.id]) || null;
        const chips = [
          { key: "total", label: "Total Piutang Terbuka", st: "outstanding_all", total: ar?.total_outstanding ?? 0, count: ar?.total_count ?? 0, cls: "border-slate-300 text-[#0B1D3A]" },          { key: "belum", label: "Belum Jatuh Tempo", st: "open", total: ar?.belum_jatuh_tempo?.total ?? 0, count: ar?.belum_jatuh_tempo?.count ?? 0, cls: "border-emerald-300 text-emerald-700" },
          { key: "lewat", label: "Invoice Lewat Jatuh Tempo", st: "overdue", total: ar?.lewat_jatuh_tempo?.total ?? 0, count: ar?.lewat_jatuh_tempo?.count ?? 0, cls: "border-rose-300 text-rose-700" },
          { key: "nodue", label: "Jatuh Tempo Belum Diisi", st: "no_due_date", total: ar?.no_due_date?.total ?? 0, count: ar?.no_due_date?.count ?? 0, cls: "border-amber-300 text-amber-700" },
          // 2026-10-18 (closeout #4): komponen klaim retensi belum dibayar —
          // bagian dari Total Piutang Terbuka, dilabel terpisah.
          { key: "klaim", label: "Klaim Retensi Menunggu Pembayaran", st: "retention_claim", total: ar?.klaim_retensi_belum_dibayar?.total ?? 0, count: ar?.klaim_retensi_belum_dibayar?.count ?? 0, cls: "border-purple-300 text-purple-700" },
          // 2026-10-19 (user request anti double-counting): klaim retensi
          // perlu verifikasi (legacy tanpa tautan sumber, invalid, over-claim).
          ...(((ar?.retention_claim_needs_verification?.count ?? 0) > 0) ? [{ key: "verif", label: "Klaim Retensi Perlu Verifikasi · Sisa", st: "retention_claim", total: ar?.retention_claim_needs_verification?.remaining_total ?? 0, count: ar?.retention_claim_needs_verification?.count ?? 0, cls: "border-rose-300 text-rose-700" }] : []),
        ];
        if (!ar || (ar.total_count === 0 && (ar.pending_verification?.count ?? 0) === 0)) {
          return <td key="ar" className="px-3 py-2 align-top"><span className="text-slate-300">—</span></td>;
        }
        return (
          <td key="ar" className="px-3 py-2 align-top" data-testid={`summary-ar-${r.id}`}>
            <div className="grid grid-cols-2 gap-1">
              {chips.map((c) => (
                <button key={c.key} type="button"
                  data-testid={`ar-chip-${c.key}-${r.id}`}
                  onClick={() => nav(`/collection?project_id=${r.id}&status=${c.st}`)}
                  title={`${c.label}: ${c.count} transaksi · ${formatIDR(c.total)} — klik untuk membuka daftar pembentuknya di Collection`}
                  className={`border ${c.cls} bg-white hover:bg-slate-50 px-1.5 py-1 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5A1F] ${c.key === "total" ? "col-span-2" : ""}`}>
                  <div className="text-[8px] uppercase tracking-wider font-semibold opacity-80 leading-tight">{c.label}</div>
                  <div className="text-[10px] font-bold tabular-nums leading-tight">{c.count} inv · {formatIDR(c.total)}</div>
                </button>
              ))}
            </div>
            {(ar.pending_verification?.count ?? 0) > 0 && (
              <div className="text-[9px] text-slate-500 mt-1" data-testid={`ar-pending-${r.id}`}>
                + {ar.pending_verification.count} draft bersisa · Sisa Draft {formatIDR(ar.pending_verification.total)} (belum piutang resmi)
              </div>
            )}
          </td>
        );
      }
      case "profit": {
        // R227 (2026-10-20, user request "SELARASKAN PROFIT, MARGIN, DAN
        // STATUS KELENGKAPAN BIAYA"): kolom Selisih memakai profit_view
        // KANONIS engine (identik dgn Monitoring & Cost Control). Baris parent
        // memakai view proyek induk (engine sudah mengagregasi VO/WO anak).
        // Label "Selisih Sementara" + badge kelengkapan; margin null →
        // "Margin belum dapat dihitung" (bukan 100%). Klik → drill-down.
        const pv = ccMap[r.id]?.profit_view || null;
        if (!pv) {
          // R227: data summaries SUDAH termuat & proyek tidak punya entri CC
          // → tabel CC kosong = status kanonis unknown ("Belum Dapat
          // Dipastikan"), BUKAN strip kosong. User tanpa akses finansial
          // (403) / masih loading → fallback tampilan lama.
          if (ccLoaded && !ccDenied && !isChild) {
            return (
              <td key="profit" className="px-3 py-2 align-top text-right tabular-nums" data-testid={`summary-profit-${r.id}`}>
                <button type="button" onClick={() => openProfitDrill(r.id)} className="inline-flex flex-col items-end gap-0.5 text-right" data-testid={`profit-idr-${r.id}`}
                  title="Tabel Cost Control kosong — klik untuk penjelasan status">
                  <span className="text-[10px] text-slate-400 italic">Belum Dapat Dipastikan</span>
                  <span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border bg-slate-100 text-slate-600 border-slate-300 hover:brightness-95" data-testid={`profit-badge-${r.id}`}>Belum Dapat Dipastikan</span>
                </button>
              </td>
            );
          }
          // Fallback legacy (masih loading / tanpa akses): perilaku lama.
          const ids = isChild ? [r.id] : [r.id, ...kids.map((k) => k.id)];
          let has = false; let kontrak = 0; let profit = 0;
          for (const id of ids) {
            const s = ccMap[id];
            if (s && s.item_count > 0) { has = true; kontrak += Number(s.total_kontrak || 0); profit += Number(s.profit || 0); }
          }
          if (!has) return <td key="profit" className="px-3 py-2 align-top text-right tabular-nums"><span className="text-slate-300">—</span></td>;
          return (
            <td key="profit" className="px-3 py-2 align-top text-right tabular-nums" data-testid={`summary-profit-${r.id}`}>
              <div className={`font-bold ${profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatIDR(profit)}</div>
              {kontrak > 0 && <div className="text-[9px] text-slate-500 tabular-nums">{((profit / kontrak) * 100).toFixed(1)}% margin</div>}
            </td>
          );
        }
        const st = pv.completeness?.status || "unknown";
        const hasCc = (pv.revenue_basis?.item_count || 0) > 0;
        if (!hasCc) {
          return (
            <td key="profit" className="px-3 py-2 align-top text-right tabular-nums" data-testid={`summary-profit-${r.id}`}>
              <button type="button" onClick={() => openProfitDrill(r.id)} className="text-left" data-testid={`profit-idr-${r.id}`}
                title="Tabel Cost Control kosong — klik untuk penjelasan status">
                <div className="text-[10px] text-slate-400 italic">Belum Dapat Dipastikan</div>
                <ProfitStatusBadge view={pv} small testid={`profit-badge-${r.id}`} />
              </button>
            </td>
          );
        }
        // R229 (2026-10-20): status UNKNOWN → tampilan utama TANPA angka
        // selisih bertanda & TANPA persentase margin. Label utama "Belum Dapat
        // Dipastikan" + teks "Margin belum dapat dihitung". Alasan/sumber data
        // yang belum tersedia dijelaskan di dialog rincian (klik).
        if (st === "unknown") {
          return (
            <td key="profit" className="px-3 py-2 align-top text-right tabular-nums" data-testid={`summary-profit-${r.id}`}>
              <button type="button" onClick={() => openProfitDrill(r.id)} className="text-right w-full" data-testid={`profit-idr-${r.id}`}
                title="Belum Dapat Dipastikan — klik untuk penjelasan status & sumber data yang belum tersedia">
                <div className="font-semibold text-slate-500 text-[11px] italic">Belum Dapat Dipastikan</div>
                <div className="text-[9px] text-slate-400" data-testid={`profit-pct-${r.id}`}>Margin belum dapat dihitung</div>
                <div className="mt-0.5 flex justify-end"><ProfitStatusBadge view={pv} small testid={`profit-badge-${r.id}`} /></div>
              </button>
            </td>
          );
        }
        const selisih = Number(pv.selisih || 0);
        return (
          <td key="profit" className="px-3 py-2 align-top text-right tabular-nums" data-testid={`summary-profit-${r.id}`}>
            <button type="button" onClick={() => openProfitDrill(r.id)} className="text-right w-full" data-testid={`profit-idr-${r.id}`}
              title={`${pv.labels?.selisih || "Selisih Sementara"} — klik untuk rincian pembentuk angka`}>
              <div className={`font-bold ${st === "complete" ? (selisih >= 0 ? "text-emerald-700" : "text-rose-700") : st === "incomplete" ? "text-amber-700" : "text-slate-600"}`}>
                {selisih >= 0 ? "+" : "−"}{formatIDR(Math.abs(selisih))}
              </div>
              <div className="text-[9px] text-slate-500 tabular-nums" data-testid={`profit-pct-${r.id}`}>
                {pv.margin === null || pv.margin === undefined
                  ? "Margin belum dapat dihitung"
                  : `${pv.margin.toFixed(1)}% ${st === "complete" ? "margin" : "margin sementara"}`}
              </div>
              {st !== "complete" && (
                <div className="mt-0.5 flex justify-end"><ProfitStatusBadge view={pv} small testid={`profit-badge-${r.id}`} /></div>
              )}
            </button>
          </td>
        );
      }
      case "aksi":
        return (
          <td key="aksi" className="pl-3 pr-2 py-2 align-top text-right">
            <div className="inline-flex flex-col items-end gap-1">
              <button type="button" data-testid={`btn-monitoring-${r.id}`}
                onClick={() => nav(`/projects/${r.id}/monitoring`)}
                title="Buka Dashboard Monitoring"
                className="inline-flex items-center gap-1 px-2 py-1 bg-[#0B1D3A] hover:bg-[#12284d] text-white text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap">
                <ExternalLink size={11} /> Monitoring
              </button>
              {/* 2026-06-20 (user request): tombol Cost Control di kolom paling kanan
                  untuk parent & sub (VO/WO) — membuka halaman tabel (bukan popup). */}
              <button type="button" data-testid={`btn-cost-control-${r.id}`}
                onClick={() => nav(`/projects/${r.id}/cost-control`)}
                title="Buka Cost Control"
                className="inline-flex items-center gap-1 px-2 py-1 border border-[#0B1D3A] text-[#0B1D3A] hover:bg-[#0B1D3A] hover:text-white text-[10px] uppercase tracking-wider font-semibold whitespace-nowrap">
                <Calculator size={11} /> Cost Control
              </button>
            </div>
          </td>
        );
      default:
        return null;
    }
  };

  const colByKey = Object.fromEntries(COLUMNS.map((c) => [c.key, c]));

  return (
    <div className="space-y-3" data-testid="summary-project-list-view">
      {/* R227/R229: drill-down rincian Selisih & kelengkapan biaya (komponen
          sama dgn Monitoring & Cost Control) — read-only. Saat ditutup, pid
          tetap tersimpan → isi TIDAK berubah jadi empty state saat closing. */}
      <ProfitDrilldownDialog
        open={profitDrillOpen}
        onOpenChange={setProfitDrillOpen}
        view={profitDrill ? (ccMap[profitDrill]?.profit_view || buildUnknownProfitView(profitDrill)) : null}
        projectName={profitDrill ? (projects.find((p) => p.id === profitDrill)?.project_name || null) : null}
      />
      {/* Filter bar */}
      <div className="bg-white border border-slate-200 p-3 space-y-2" data-testid="summary-filter-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={13} className="absolute left-2.5 top-2 text-slate-400 pointer-events-none" />
            <input type="text" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Cari nama proyek, client, PIC, nomor kontrak/WO/PO…"
              data-testid="summary-search-input"
              className="w-full pl-8 pr-8 py-1.5 border border-slate-300 text-xs focus:outline-none focus:border-[#0B1D3A]" />
            {q && (<button type="button" data-testid="summary-search-clear" onClick={() => setQ("")}
              className="absolute right-2 top-2 text-slate-400 hover:text-[#FF5A1F]"><X size={13} /></button>)}
          </div>
          <MultiFilter label="Client" options={clients} sel={clientSel} mode={clientMode}
            onSelChange={(s) => { setClientSel(s); setPage(1); }} onModeChange={(m) => { setClientMode(m); setPage(1); }}
            withMode testid="filter-client" />
          <MultiFilter label="PIC" options={pics} sel={picSel} mode={picMode}
            onSelChange={(s) => { setPicSel(s); setPage(1); }} onModeChange={(m) => { setPicMode(m); setPage(1); }}
            withMode testid="filter-pic" />
          <MultiFilter label="Status" options={["active", "standby", "overdate", "close", "cancelled_by_owner"]} sel={statusSel}
            onSelChange={(s) => { setStatusSel(s); setPage(1); }} testid="filter-status" />
          {/* 2026-06-20 (user request): filter Status Operasional — Pekerjaan
              Berjalan / Selesai Fisik · Adm / Masa Retensi / Siap Ditutup /
              Closed / Dibatalkan. */}
          <MultiFilter label="Status Operasional" options={OP_STATUS_ORDER.map((k) => OP_STATUS_META[k].label)} sel={opsSel}
            onSelChange={(s) => { setOpsSel(s); setPage(1); }} testid="filter-op-status" />
          {/* R228 (2026-10-20): filter "Risiko" dihapus — fitur penilaian
              risiko dinonaktifkan sementara. */}
          <div className="flex items-center gap-1 text-[11px] text-slate-500">
            <span>Periode</span>
            <input type="date" value={fPeriod.from} onChange={(e) => { setFPeriod({ ...fPeriod, from: e.target.value }); setPage(1); }}
              data-testid="filter-period-from" className="border border-slate-300 py-1 px-1.5 text-xs bg-white" />
            <span>–</span>
            <input type="date" value={fPeriod.to} onChange={(e) => { setFPeriod({ ...fPeriod, to: e.target.value }); setPage(1); }}
              data-testid="filter-period-to" className="border border-slate-300 py-1 px-1.5 text-xs bg-white" />
          </div>
          {hasAnyFilter ? (
            <button type="button" data-testid="filter-reset" onClick={resetFilters}
              className="text-[10px] text-slate-500 hover:text-[#FF5A1F] underline uppercase tracking-wider">Reset</button>
          ) : null}
          <div className="ml-auto flex items-center gap-2 text-[11px] text-slate-500 tabular-nums" data-testid="summary-count">
            <FilterIcon size={12} /> {total.toLocaleString("id-ID")} proyek utama dari {parents.length.toLocaleString("id-ID")}{totalSubCount > 0 ? ` · ${totalSubCount.toLocaleString("id-ID")} sub (VO/WO)` : ""}
          </div>
        </div>
        <div className="text-[10px] text-slate-400">Tips: klik judul kolom untuk sort · geser (drag) judul kolom untuk mengubah posisi kolom.</div>
      </div>

      {loadState === "error" && (
        <div className="bg-rose-50 border border-rose-300 p-3 flex items-center justify-between gap-3" data-testid="summary-error">
          <div className="text-xs text-rose-800">{loadError || "Gagal memuat data project."}</div>
          <button type="button" onClick={onRetry} data-testid="summary-retry" className="px-3 py-1.5 text-[11px] uppercase tracking-wider border border-rose-500 text-rose-700 hover:bg-rose-100">Coba Lagi</button>
        </div>
      )}

      {/* Table full-width */}
      <div className="bg-white border border-slate-200">
        <table className="w-full text-xs table-fixed">
          <thead className="bg-slate-50 border-b-2 border-slate-200">
            <tr className="text-left">
              {colOrder.map((k) => {
                const c = colByKey[k];
                const isSorted = sort.key === k;
                return (
                  <th key={k}
                    draggable
                    onDragStart={() => { dragKey.current = k; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => dropCol(k)}
                    onClick={() => c.sortable && clickSort(k)}
                    data-testid={`th-${k}`}
                    title={c.sortable ? "Klik untuk sort · drag untuk geser kolom" : "Drag untuk geser kolom"}
                    className={`px-3 py-2 overline text-slate-600 select-none ${c.thCls} ${c.sortable ? "cursor-pointer hover:text-[#0B1D3A]" : "cursor-grab"} ${isSorted ? "text-[#0B1D3A]" : ""}`}>
                    <span className="inline-flex items-center gap-1">
                      <GripVertical size={10} className="text-slate-300" />
                      {c.label}
                      {c.sortable && (isSorted
                        ? (sort.dir === 1 ? <ArrowUp size={11} /> : <ArrowDown size={11} />)
                        : <ChevronsUpDown size={11} className="text-slate-300" />)}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody data-testid="summary-project-tbody">
            {loadState === "loading" && paged.length === 0 && (
              <tr><td colSpan={colOrder.length} className="px-3 py-8 text-center text-slate-400 text-[11px]" data-testid="summary-loading">Memuat data project…</td></tr>
            )}
            {(loadState === "success" || loadState === "empty" || loadState === "refetching") && total === 0 && (
              <tr><td colSpan={colOrder.length} className="px-3 py-10 text-center text-slate-400" data-testid="summary-empty">
                <Users size={24} className="mx-auto mb-2 text-slate-300" />
                <div className="text-xs">Tidak ada proyek yang cocok dengan filter aktif.</div>
                {canCreate && !hasAnyFilter && (
                  <button type="button" data-testid="summary-empty-cta" onClick={onOpenNewProject}
                    className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 bg-[#FF5A1F] hover:bg-[#e04a13] text-white text-[11px] uppercase tracking-wider font-semibold">+ New Project</button>
                )}
              </td></tr>
            )}
            {paged.map((p) => {
              const kids = childrenOf[p.id] || [];
              const isOpen = expanded.has(p.id);
              const agg = aggMap[p.id] || aggOf(p);
              const ctx = { isChild: false, kids, isOpen, agg };
              return [
                <tr key={p.id} data-testid={`summary-row-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  {colOrder.map((k) => cellFor(p, k, ctx))}
                </tr>,
                ...(isOpen ? kids.map((c) => (
                  <tr key={c.id} data-testid={`summary-row-${c.id}`} className="border-b border-slate-100 hover:bg-slate-50 bg-slate-50/50">
                    {colOrder.map((k) => cellFor(c, k, { isChild: true, kids: [], isOpen: false, agg: null }))}
                  </tr>
                )) : []),
              ];
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="flex items-center justify-between gap-2 flex-wrap text-[11px] text-slate-500" data-testid="summary-pagination">
          <div className="flex items-center gap-1.5">
            <span>Halaman</span>
            <button type="button" data-testid="page-prev" disabled={curPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-2 py-1 border border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50">‹</button>
            <span className="tabular-nums font-semibold text-[#0B1D3A]">{curPage}</span>
            <span>/ {totalPages}</span>
            <button type="button" data-testid="page-next" disabled={curPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-2 py-1 border border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50">›</button>
          </div>
          <div className="flex items-center gap-2">
            <span>Baris per halaman</span>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              data-testid="page-size" className="border border-slate-300 py-1 px-1.5 text-xs bg-white">
              {PAGE_SIZE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <span className="tabular-nums">Menampilkan {((curPage - 1) * pageSize + 1).toLocaleString("id-ID")}–{Math.min(curPage * pageSize, total).toLocaleString("id-ID")} dari {total.toLocaleString("id-ID")}</span>
          </div>
        </div>
      )}
    </div>
  );
}
